import { useCallback, useEffect, useState } from 'react';

import { VehicleSize } from '@cobliteam/common-types';

import { ReactComponent as FilterIcon } from '@cobliteam/ui/icons/filters/filter_alt_cb.svg';

import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';

import Checkbox from '../../../form/Checkbox';
import Label from '../../../form/Label';

export interface MultiSelectVehicleSizeProps {
  isOpen?: boolean;
  size?: 'condensed' | 'regular';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const getVehicleSizeLabel = (size: VehicleSize) => {
  switch (size) {
    case VehicleSize.SMALL:
      return 'Pequeno';
    case VehicleSize.MEDIUM:
      return 'Médio';
    case VehicleSize.BIG:
      return 'Grande';
    default:
      return 'Desconhecido';
  }
};

const MultiSelectVehicleSize = ({ isOpen, size = 'regular', selectedOptions = [], onChange }: MultiSelectVehicleSizeProps) => {
  const [forceClosed, setForceClosed] = useState(false);
  const [filter, setFilter] = useState(selectedOptions || []);

  const vehicleSizes = Object.values(VehicleSize);

  // filter search
  const filterOptions = vehicleSizes.map((type: VehicleSize) => ({
    key: type,
    label: getVehicleSizeLabel(type),
  }));

  // check if all selected types are in filter
  const hasSameElements = selectedOptions?.length === filter?.length && selectedOptions.every((value) => filter.find((d) => d === value));

  const onClick = useCallback(() => {
    setFilter(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = filter.find((d) => d === type);
    const updatedSelected = isSelected ? filter.filter((d) => d !== type) : [...filter, type];
    setFilter(updatedSelected as VehicleSize[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setFilter([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(filter);
    handleClose();
  }, [filter, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='text' size='condensed' disabled={filter?.length === 0} onClick={() => setFilter([])}>
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='ml-5'>
            <Dropdown.List
              heightList={300}
              heightItem={40}
              data={filterOptions || []}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex gap-2 items-center'>
                  <Checkbox id={item.key} key={item.key} checked={filter.some((d) => d === item.key)} onCheckedChange={() => onChangeSelect(item.key)} />
                  <Label htmlFor={item.key}>{item.label}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button full variant='text' size='condensed' onClick={handleClose}>
                Cancelar
              </Button>
              <Button full variant='contained' size='condensed' color='primary' onClick={handleApply} disabled={hasSameElements}>
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<FilterIcon />}>
        Porte {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectVehicleSize;
